import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function ContactForm() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams(formData as any);

    try {
      await fetch('https://readdy.ai/api/form/d5fplmn1sgdus1eera80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitStatus('success');
      form.reset();
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-24 bg-cream">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-5xl text-charcoal mb-4">
            Hadi Başlayalım
          </h2>
          <p className="font-sans text-lg text-gray-medium">
            Formu doldurun, size en kısa sürede dönelim.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          id="brief-form"
          data-readdy-form
          action="https://readdy.ai/api/form/d5fplmn1sgdus1eera80"
          method="POST"
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl p-8 md:p-12 shadow-lg"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block font-sans text-sm font-medium text-charcoal mb-2">
                İsim Soyisim *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:border-charcoal transition-colors"
              />
            </div>

            <div>
              <label htmlFor="company" className="block font-sans text-sm font-medium text-charcoal mb-2">
                Şirket *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:border-charcoal transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="email" className="block font-sans text-sm font-medium text-charcoal mb-2">
                E-posta *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:border-charcoal transition-colors"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block font-sans text-sm font-medium text-charcoal mb-2">
                Telefon *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:border-charcoal transition-colors"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="need" className="block font-sans text-sm font-medium text-charcoal mb-2">
              İhtiyacınız *
            </label>
            <select
              id="need"
              name="need"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:border-charcoal transition-colors cursor-pointer"
            >
              <option value="">Seçiniz</option>
              <option value="strategy">Strategy</option>
              <option value="social-media">Social Media</option>
              <option value="influencer">Influencer Marketing</option>
              <option value="ai">AI Studio</option>
              <option value="experience">Experience</option>
              <option value="creative-content">Creative Content Üretimi</option>
              <option value="production">Prodüksiyon (Video, Fotoğraf) Hizmeti</option>
              <option value="multiple">Birden fazla hizmet</option>
            </select>
          </div>

          <div className="mb-8">
            <label htmlFor="message" className="block font-sans text-sm font-medium text-charcoal mb-2">
              Mesajınız *
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg font-sans text-sm focus:outline-none focus:border-charcoal transition-colors resize-none"
              placeholder="Projeniz hakkında bize kısaca bilgi verin..."
            ></textarea>
            <p className="text-xs text-gray-light mt-1">Maksimum 500 karakter</p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-sans text-sm text-green-800">
                Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-sans text-sm text-red-800">
                Bir hata oluştu. Lütfen tekrar deneyin veya doğrudan e-posta gönderin.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-charcoal text-white py-4 rounded-full font-sans font-medium text-sm hover:bg-charcoal/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? 'Gönderiliyor...' : 'Brief Gönder'}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
